import { useEffect, useState } from "react";
import { t } from "@/i18n-js/instance";
import { usePaywallGroupApi } from "@/react/components/PaywallGroups/Admin/hooks/usePaywallGroupApi";
import { Form } from "@/react/components/shared/uikit/Form";
import type { PaywallGroupProps } from "@/react/types/PaywallGroup";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { Loader } from "@circle-react-uikit/Loader";
import { Modal } from "@circle-react-uikit/ModalV2";
import { Typography } from "@circle-react-uikit/Typography";
import { FormContent } from "./FormContent";
import { MIGRATION_FLOW_OPTIONS } from "./FormContent/constants";

interface MigrationModalType {
  closePaywallFormModal: () => void;
  currencyId: number;
  paywallGroupId?: number;
  paywallId: number;
}

const i18nBase = "settings.access_groups.paywall_migration.modal";

export const MigrationContent = ({
  paywallId,
  closePaywallFormModal,
  paywallGroupId,
  currencyId,
}: MigrationModalType) => {
  const submitHandler = () => {};

  const getDefaultValues = () => ({
    paywall_id: paywallId,
    access_groups: [],
    migration_flow: MIGRATION_FLOW_OPTIONS.new_access_group,
    paywall_group_id: "",
    paywall_group_name: "",
  });
  const modal = useModal();

  const { useListPaywallGroups } = usePaywallGroupApi({});
  const { data: paywallGroups = [], isLoading: isLoadingPaywallGroups } =
    useListPaywallGroups();

  const currentPaywallGroup = paywallGroups.find(
    (group: PaywallGroupProps) => group.id === paywallGroupId,
  );

  const [shouldShowPaywallGroupSelector, setShouldShowPaywallGroupSelector] =
    useState(false);

  useEffect(() => {
    if (currentPaywallGroup) {
      setShouldShowPaywallGroupSelector(
        (currentPaywallGroup.upgradable_paywalls?.length ?? 0) > 1,
      );
    }
  }, [currentPaywallGroup]);

  if (isLoadingPaywallGroups) {
    return <Loader />;
  }

  const closeModals = () => {
    void modal.hide();
    closePaywallFormModal();
  };

  return (
    <Modal isOpen={modal.visible} onClose={modal.hide}>
      <Modal.Overlay />
      <Modal.Content size="xl">
        <Modal.Header>
          <Modal.Title size="sm">
            <Typography.TitleSm weight="bold">
              {t([i18nBase, "title"])}
            </Typography.TitleSm>
          </Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={modal.hide} />
          </Modal.HeaderActions>
        </Modal.Header>
        <div>
          <Form onSubmit={submitHandler} defaultValues={getDefaultValues()}>
            <FormContent
              closeModals={closeModals}
              currencyId={currencyId}
              shouldShowPaywallGroupSelector={shouldShowPaywallGroupSelector}
            />
          </Form>
        </div>
      </Modal.Content>
    </Modal>
  );
};

const MigrationModal = ModalManager.create(
  ({
    paywallId,
    closePaywallFormModal,
    paywallGroupId,
    currencyId,
  }: MigrationModalType) => (
    <MigrationContent
      paywallId={paywallId}
      closePaywallFormModal={closePaywallFormModal}
      paywallGroupId={paywallGroupId}
      currencyId={currencyId}
    />
  ),
);

export const useMigrationModal = () => useModal(MigrationModal);
